<template>
  <VaNavbar class="app-layout-navbar px-0">
    <template #left>
      <div class="left">
        <Transition v-if="isMobile" name="icon-fade" mode="out-in">
          <button
            type="button"
            class="nav-control mobile-menu-toggle"
            :aria-label="isSidebarMinimized ? 'Open menu' : 'Close menu'"
            @click="isSidebarMinimized = !isSidebarMinimized"
          >
            <VaIcon
              color="primary"
              :name="isSidebarMinimized ? 'menu' : 'close'"
              size="20px"
            />
          </button>
        </Transition>
        <RouterLink to="/" class="navbar-logo-link" aria-label="Visit home page">
          <VuesticLogo :inline="true" :height="isMobile ? 30 : 36" />
        </RouterLink>
      </div>
    </template>
    <template #right>
      <AppNavbarActions class="app-navbar__actions" :is-mobile="isMobile" />
    </template>
  </VaNavbar>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import AppNavbarActions from './components/AppNavbarActions.vue'
import VuesticLogo from '../VuesticLogo.vue'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const GlobalStore = useGlobalStore()

const { isSidebarMinimized } = storeToRefs(GlobalStore)
</script>

<style lang="scss" scoped>
.va-navbar {
  --nav-control-size: 38px;
  --nav-radius: 12px;
  z-index: 2;
  width: 100%;
  min-height: 64px;
  padding: 0;
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--va-background-primary) 92%, #ffffff 8%) 0%,
      color-mix(in srgb, var(--va-background-primary) 86%, transparent) 100%
    );
  backdrop-filter: blur(18px) saturate(1.35);
  -webkit-backdrop-filter: blur(18px) saturate(1.35);
  border-bottom: 1px solid color-mix(in srgb, var(--va-background-border) 55%, transparent);
  box-shadow:
    0 1px 0 color-mix(in srgb, #fff 4%, transparent),
    0 8px 24px rgba(15, 23, 42, 0.06);

  :deep(.va-navbar__content) {
    width: 100%;
    gap: 0.75rem;
    align-items: center;
    padding: 0.55rem 1.15rem;
  }

  :deep(.va-navbar__left),
  :deep(.va-navbar__right) {
    min-width: 0;
  }

  :deep(.va-navbar__left) {
    flex: 1 1 auto;
  }

  :deep(.va-navbar__right) {
    display: flex;
    justify-content: flex-end;
    flex: 0 1 auto;
  }

  @media screen and (max-width: 950px) {
    min-height: 58px;
    --nav-control-size: 36px;

    :deep(.va-navbar__content) {
      padding: 0.5rem 0.85rem;
    }
  }

  @media screen and (max-width: 640px) {
    min-height: 54px;
    --nav-control-size: 34px;
    --nav-radius: 10px;

    :deep(.va-navbar__content) {
      padding: 0.45rem 0.7rem;
    }
  }
}

.left {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
  flex: 1 1 auto;
}

.nav-control {
  flex-shrink: 0;
  width: var(--nav-control-size);
  height: var(--nav-control-size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--va-background-border) 65%, transparent);
  border-radius: var(--nav-radius);
  background: color-mix(in srgb, var(--va-background-element) 55%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, #fff 6%, transparent);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--va-primary) 12%, var(--va-background-element));
    border-color: color-mix(in srgb, var(--va-primary) 35%, var(--va-background-border));
    box-shadow:
      inset 0 1px 0 color-mix(in srgb, #fff 8%, transparent),
      0 4px 14px color-mix(in srgb, var(--va-primary) 12%, transparent);
  }

  &:active {
    transform: translateY(1px) scale(0.97);
  }
}

.navbar-logo-link {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.25rem;
  border-radius: 10px;
  transition: opacity 0.18s ease, transform 0.18s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.icon-fade-enter-from,
.icon-fade-leave-to {
  opacity: 0;
  transform: scale(0.88);
}
</style>
